import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, Toolbar } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import SideNav from './components/SideNav';
import TopNav from './components/TopNav';
import PrivateRoute from './components/PrivateRoute';
import './styles/App.css';
import DashboardPage from './pages/DashboardPage';

// ✅ Updated Customer imports
import CustomersPage from './features/customers/pages/CustomersPage';
import CustomerDetailPage from './features/customers/pages/CustomerDetailPage';
import AddCustomerPage from './features/customers/pages/AddCustomerPage';

import ProductsListPage from './features/product/pages/ProductsListPage';  
import ProposalsPage from './pages/ProposalsPage';
import CreditNotesPage from './pages/CreditNotesPage';
import PaymentsPage from './pages/PaymentsPage';
import PurchaseOrdersListPage from './features/purchases/pages/PurchaseOrdersListPage';
import ReportsPage from './features/reports/pages/ReportsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AnnouncementPage from './features/announcements/pages/AnnouncementPage';

import 'react-toastify/dist/ReactToastify.css';
import ListInvoices from './features/invoices/pages/ListInvoices';
import AddInvoicePage from './features/invoices/pages/AddInvoicePage';
import InvoiceViewPage from './features/invoices/pages/InvoiceViewPage';
import ProductDetailPage from './features/product/pages/ProductDetailPage';
import AddProductPage from './features/product/pages/AddProductPage';
import CompanySettingsPage from './features/companysettings/pages/CompanySettingsPage';
import ProfileSettingsPage from './features/companysettings/pages/ProfileSettingsPage';
import ListSuppliers from './features/suppliers/pages/ListSuppliers';
import AddSupplier from './features/suppliers/pages/AddSupplier';
import EditSupplier from './features/suppliers/pages/EditSupplier';
import AddCreditNotePage from './features/creditnotes/pages/AddCreditNotePage';
import ListCreditNotesPage from './features/creditnotes/pages/ListCreditNotesPage';
import CreditNoteDetailPage from './features/creditnotes/pages/CreditNoteDetailPage';

import AddOfferPage from './features/offers/pages/AddOfferPage';
import ListOffersPage from './features/offers/pages/ListOffersPage';
import OfferDetailPage from './features/offers/pages/OfferDetailPage';
import "./locales/i18n";
import WishlistPage from './features/wishlist/pages/WishlistPage';
import NotFoundPage from './pages/NotFoundPage';

import AddPurchaseOrderPage from "./features/purchases/pages/AddPurchaseOrderPage";
import PurchaseOrderDetailPage from "./features/purchases/pages/PurchaseOrderDetailPage";
import ComingSoon from './pages/ComingSoon';
import ShopifyProductsListPage from './features/shopify/pages/ShopifyProductsListPage';
import AddShopifyProduct from './features/shopify/pages/AddShopifyProduct';
import ShopifyProductDetailPage from './features/shopify/pages/ShopifyProductDetailPage';
import EditShopifyProductPage from './features/shopify/pages/EditShopifyProductPage';
import PosListPage from './features/pos/pages/PosListPage';
import { initializeAuth } from './services/authService';

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const isAuthRoute = ['/', '/login', '/register', '/forgot-password'].includes(location.pathname);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <>
      {!isAuthRoute ? (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          <TopNav onMenuClick={handleDrawerToggle} />
          <SideNav mobileOpen={mobileOpen} onMobileClose={handleDrawerToggle} />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 0, // ✅ Remove all padding from main container
              width: '100%', // ✅ Take full available width
              ml: 0, // ✅ Remove left margin
              mt: { xs: '64px', sm: '70px' }, // ✅ Only top margin for TopNav
            }}
          >
            <Routes>
              {/* ✅ Dashboard */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <DashboardPage />
                  </PrivateRoute>
                }
              />

              {/* ✅ Updated Customer Routes */}
              <Route
                path="/customers"
                element={
                  <PrivateRoute>
                    <CustomersPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/customers/add"
                element={
                  <PrivateRoute>
                    <AddCustomerPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/customers/edit/:id"
                element={
                  <PrivateRoute>
                    <AddCustomerPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/customers/detail/:id"
                element={
                  <PrivateRoute>
                    <CustomerDetailPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/customer/:id"
                element={
                  <PrivateRoute>
                    <CustomerDetailPage />
                  </PrivateRoute>
                }
              />

              {/* ✅ Product Routes */}
              <Route
                path="/products"
                element={
                  <PrivateRoute>
                    <ProductsListPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/products/add"
                element={
                  <PrivateRoute>
                    <AddProductPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/products/edit/:id"
                element={
                  <PrivateRoute>
                    <AddProductPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/products/detail/:id"
                element={
                  <PrivateRoute>
                    <ProductDetailPage />
                  </PrivateRoute>
                }
              />

              {/* ✅ Invoice Routes */}
              <Route
                path="/invoices"
                element={
                  <PrivateRoute>
                    <ListInvoices />
                  </PrivateRoute>
                }
              />
              <Route
                path="/invoices/add"
                element={
                  <PrivateRoute>
                    <AddInvoicePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/invoices/view/:id"
                element={
                  <PrivateRoute>
                    <InvoiceViewPage />
                  </PrivateRoute>
                }
              />

              {/* ✅ Supplier Routes */}
              <Route
                path="/suppliers"
                element={
                  <PrivateRoute>
                    <ListSuppliers />
                  </PrivateRoute>
                }
              />
              <Route path="/profile-settings" element={<PrivateRoute><ProfileSettingsPage /></PrivateRoute>} /> {/* ✅ Add this route */}
              <Route
                path="/suppliers/add"
                element={
                  <PrivateRoute>
                    <AddSupplier />
                  </PrivateRoute>
                }
              />
              <Route
                path="/suppliers/edit/:id"
                element={
                  <PrivateRoute>
                    <EditSupplier />
                  </PrivateRoute>
                }
              />

              {/* ✅ Credit Notes Routes */}
              <Route
                path="/sales/credit-notes"
                element={
                  <PrivateRoute>
                    <ListCreditNotesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/credit-notes/add"
                element={
                  <PrivateRoute>
                    <AddCreditNotePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/credit-notes/:id"
                element={
                  <PrivateRoute>
                    <CreditNoteDetailPage />
                  </PrivateRoute>
                }
              />

              {/* ✅ Offers / Proposals Routes */}
              <Route
                path="/sales/offers"
                element={
                  <PrivateRoute>
                    <ListOffersPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/offers/add"
                element={
                  <PrivateRoute>
                    <AddOfferPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/offers/:id"
                element={
                  <PrivateRoute>
                    <OfferDetailPage />
                  </PrivateRoute>
                }
              />

              {/* ✅ Other Routes */}
              <Route
                path="/purchases"
                element={
                  <PrivateRoute>
                    <PurchaseOrdersListPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/reports"
                element={<ReportsPage />}
              />
              <Route
                path="/payments"
                element={
                  <PrivateRoute>
                    <ComingSoon title="Payments" description="Payment processing and management features are coming soon" />
                  </PrivateRoute>
                }
              />
              <Route
                path="/company-settings"
                element={
                  <PrivateRoute>
                    <CompanySettingsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/announcements"
                element={
                  <PrivateRoute>
                    <AnnouncementPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/wishlist"
                element={
                  <PrivateRoute>
                    <WishlistPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/purchases/add"
                element={
                  <PrivateRoute>
                    <AddPurchaseOrderPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/purchases/:id"
                element={
                  <PrivateRoute>
                    <PurchaseOrderDetailPage />
                  </PrivateRoute>
                }
              />
            
              <Route
                path="/shopify/products"
                element={<ShopifyProductsListPage />}
              />
              <Route
                path="/shopify/products/add"
                element={<AddShopifyProduct />}
              />
              <Route
                path="/shopify/products/view/:productId"
                element={<ShopifyProductDetailPage />}
              />
              <Route
                path="/shopify/products/edit/:productId"
                element={<EditShopifyProductPage />}
              />
              <Route path="/pos" element={<PosListPage />} />

              {/* ✅ 404 Catch-All Route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Box>
        </Box>
      ) : (
        <Routes>
          {/* ✅ Auth Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/" element={<LoginPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      )}
    </>
  );
};

function App() {
  useEffect(() => {
    // ✅ Initialize auth on app startup
    initializeAuth();
  }, []);

  return (
    <Router>
      <div
        style={{
          minHeight: '100vh',
          width: '100%',
          background: '#fafcff',
          backgroundImage: 'url("https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80")',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(250,252,255,0.9)',
            zIndex: 0,
          }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <AppContent />
          <ToastContainer />
        </div>
      </div>
    </Router>
  );
};

export default App;